/*
 * =============================================================================
 * App.js — Root Component for the Evolunic EIS Agent Platform (Web Frontend)
 * =============================================================================
 *
 * ROLE:
 *   This file defines three things:
 *     1. `TabPanel`  — Generic MUI tab panel wrapper (currently used only by convention).
 *     2. `App`       — Top-level provider tree and route definitions.
 *     3. `AppContent` — The authenticated shell: header, sidebar nav, section views,
 *                        chat panel, and activity panel.
 *
 * KEY DEPENDENCIES:
 *   - React Router (BrowserRouter) — client-side routing for auth pages + main app
 *   - MUI (Material UI)            — all visual components and theming
 *   - AuthContext (useAuth)         — provides `user`, `logout`, `isDevMode`
 *   - ActivityContext (useActivity) — provides `addActivity`, `triggerRefresh`
 *   - LanguageContext               — i18n support via `useLanguage`
 *   - ThemeContext                  — dark/light mode toggle
 *   - `api` service (./services/api) — all backend HTTP calls
 *
 * DATA FLOW SUMMARY:
 *   AuthContext.user  -->  currentUser (local mirror)  -->  child components
 *   api.getProjects() -->  allProjects                 -->  HeaderProjectSwitcher, ProjectDashboard
 *   api.getBranches() -->  branches                    -->  ProjectView, engineering views
 *   selectedProject   -->  projectId prop to all section views + ChatContainer context
 *   currentBranch     -->  branchId prop to all section views + ChatContainer context
 *   sessionId         -->  ChatContainer, CADViewerView (links chat messages to a session)
 *
 * localStorage KEYS (managed in this file):
 *   'eis_session_id'               — persists chat session ID across page reloads
 *   'eis_session_user'             — user ID tied to the session (security: invalidates on user switch)
 *   'eis_activeSection'            — last active sidebar section (e.g. 'projects', 'cad')
 *   'eis_selectedProjectId'        — last selected project ID for restore on reload
 *   'recentProjects'               — JSON array of up to 5 recent project IDs for quick switcher
 *   'branch_<projectId>'           — last selected branch per project (dynamic key)
 *
 *   WARNING: ErrorBoundary (./components/common/ErrorBoundary.js) also resets
 *   'eis_activeSection' to 'projects' on crash recovery. Changing that key name
 *   here requires updating ErrorBoundary too.
 *
 *   WARNING: LegalPage references 'eis_session_id' in its cookie/storage disclosure.
 *   Renaming that key requires updating LegalPage as well.
 *
 * SIDE-EFFECT NOTES:
 *   - Changing the provider nesting order in `App` can break context access.
 *     AuthProvider must be inside Router (it uses useNavigate internally).
 *   - `selectedProject` drives both the project detail view AND the projectId
 *     passed to every engineering view. Setting it to null clears project scope
 *     for parts, CAD, simulations, documents, and requirements.
 *   - `currentBranch` is persisted per-project. Changing the storage key format
 *     ('branch_<projectId>') will orphan saved preferences.
 * =============================================================================
 */

import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Box,
  Tabs,
  Tab,
  AppBar,
  Toolbar,
  Breadcrumbs,
  Link,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  useTheme,
  CircularProgress,
} from '@mui/material';
import { ArrowBack, Logout, Person, Settings, Palette, Language } from '@mui/icons-material';
import EvolunicLogo from './components/common/EvolunicLogo';
import ChatContainer from './components/chat/ChatContainer';
import GlobalNavigation from './components/layout/GlobalNavigation';
import BottomActivityPanel from './components/layout/BottomActivityPanel';
import HeaderProjectSwitcher from './components/layout/HeaderProjectSwitcher';
import { api } from './services/api';
import { ActivityProvider, useActivity } from './context/ActivityContext';
import { NotificationProvider } from './context/NotificationContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import SettingsPage from './pages/SettingsPage';
import LegalPage from './pages/LegalPage';
import HelpCenterPage from './pages/HelpCenterPage';
import ChangelogPage from './pages/ChangelogPage';
import CookieConsentBanner from './components/common/CookieConsentBanner';
import { ThemeProvider } from './context/ThemeContext';
import { LanguageProvider, useLanguage } from './context/LanguageContext';
import ErrorBoundary from './components/common/ErrorBoundary';
import { TourProvider, useTour } from './components/help/TourProvider';
import HelpButton from './components/help/HelpButton';
import WelcomeDialog from './components/onboarding/WelcomeDialog';
import OnboardingChecklist from './components/onboarding/OnboardingChecklist';
import SpotlightGuide from './components/onboarding/SpotlightGuide';
import ProjectTutorial from './components/onboarding/ProjectTutorial';
import { OnboardingProvider } from './context/OnboardingContext';
import { JobPollerProvider } from './context/JobPollerContext';
import { BillingProvider } from './context/BillingContext';
import PaywallPrompt from './components/billing/PaywallPrompt';
import CreditBalanceBadge from './components/billing/CreditBalanceBadge';
import BillingReturnPage from './components/billing/BillingReturnPage';
import { initPostHog, identifyUser, resetUser, trackEvent } from './analytics/posthog';

// Lazy-loaded section views — reduces initial bundle by ~60%
const ProjectDashboard = lazy(() => import('./components/dashboard/ProjectDashboard'));
const ProjectView = lazy(() => import('./components/projects/ProjectView'));
const UnifiedCADView = lazy(() => import('./components/cad/UnifiedCADView'));
const DocumentsView = lazy(() => import('./components/engineering/DocumentsView'));
const RequirementsView = lazy(() => import('./components/engineering/RequirementsView'));
const OrchestratorView = lazy(() => import('./components/orchestrator/OrchestratorView'));
const CADViewerView = lazy(() => import('./components/cad-agent/CADViewerView'));
const ComplianceView = lazy(() => import('./components/compliance/ComplianceView'));
const VerificationView = lazy(() => import('./components/engineering/VerificationView'));
const KnowledgeBaseView = lazy(() => import('./components/knowledge-base/KnowledgeBaseView'));
const AnalyticsView = lazy(() => import('./components/analytics/AnalyticsView'));

/*
 * TabPanel — Generic accessible tab panel wrapper.
 * Only renders children when `value === index` to avoid mounting hidden content.
 * Currently referenced by convention; not actively used in AppContent's
 * section-based routing (which uses `activeSection` string matching instead).
 */
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

/*
 * App — Root component. Builds the provider tree and defines top-level routes.
 *
 * Provider nesting order matters:
 *   ErrorBoundary > ThemeProvider > LanguageProvider > Router > AuthProvider > ...
 *
 * WARNING: AuthProvider MUST be inside Router because it calls useNavigate()
 * internally to redirect on login/logout. Moving it outside Router will crash.
 *
 * WARNING: ActivityProvider wraps all routes so that both protected and public
 * pages can potentially log activity. CookieConsentBanner is rendered outside
 * Routes so it appears on every page including auth pages.
 */
function App() {
  return (
    <ErrorBoundary>
    <ThemeProvider>
      <LanguageProvider>
        <Router>
          <AuthProvider>
            <BillingProvider>
            <NotificationProvider>
              <ActivityProvider>
              <TourProvider>
              <OnboardingProvider>
                <PaywallPrompt />
                <Routes>
                  {/* --- Public routes (no auth required) --- */}
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/register" element={<RegisterPage />} />
                  <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="/reset-password" element={<ResetPasswordPage />} />
                  <Route path="/verify-email" element={<VerifyEmailPage />} />
                  <Route path="/legal" element={<LegalPage />} />

                  {/* --- Protected routes (redirect to /login if not authenticated) --- */}
                  <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
                  <Route path="/help" element={<ProtectedRoute><HelpCenterPage /></ProtectedRoute>} />
                  <Route path="/changelog" element={<ProtectedRoute><ChangelogPage /></ProtectedRoute>} />

                  {/* Stripe redirects back here after Checkout. The component
                      triggers a BillingContext refresh + toast, then navigates
                      to `/` — avoiding a dead URL the user doesn't understand. */}
                  <Route path="/billing/success" element={<ProtectedRoute><BillingReturnPage type="success" /></ProtectedRoute>} />
                  <Route path="/billing/cancel" element={<ProtectedRoute><BillingReturnPage type="cancel" /></ProtectedRoute>} />

                  {/*
                   * Catch-all: any path not matched above lands in AppContent.
                   * AppContent handles its own internal "routing" via activeSection state
                   * rather than URL-based routing, so all authenticated pages share one URL.
                   * Side-effect: browser back/forward does NOT navigate between sections.
                   */}
                  <Route
                    path="/*"
                    element={
                      <ProtectedRoute>
                        <AppContent />
                      </ProtectedRoute>
                    }
                  />
                </Routes>
                {/* Cookie consent banner shown on all pages (public + protected) */}
                <CookieConsentBanner />
              </OnboardingProvider>
              </TourProvider>
              </ActivityProvider>
            </NotificationProvider>
            </BillingProvider>
          </AuthProvider>
        </Router>
      </LanguageProvider>
    </ThemeProvider>
    </ErrorBoundary>
  );
}

/*
 * AppContent — The main authenticated application shell.
 *
 * Responsible for:
 *   - Session management (sessionId generation + localStorage persistence)
 *   - Project and branch selection (global context for all engineering views)
 *   - Section-based navigation (sidebar drives activeSection state)
 *   - Layout: fixed header, left sidebar (GlobalNavigation), main content, chat sidebar
 *   - User menu (settings, sign out)
 *
 * This component is the single source of truth for:
 *   - selectedProject (which project is active)
 *   - currentBranch   (which branch within that project)
 *   - activeSection   (which sidebar section is displayed)
 * All child views receive these as props; they do NOT manage their own project/branch state.
 */
function AppContent() {
  /* --- Core state --- */
  const [sessionId, setSessionId] = useState('');     // Unique session ID for chat; persisted in localStorage
  const [activeTab, setActiveTab] = useState(0);      // Tab index (legacy; not actively driving UI — see activeSection)
  const [uploadedFiles, setUploadedFiles] = useState([]); // Accumulated uploaded files passed to ChatContainer
  const { addActivity, triggerRefresh, refreshTrigger } = useActivity();
  const { user, logout, isDevMode } = useAuth();      // `user` is the auth source of truth from AuthContext
  const navigate = useNavigate();
  const theme = useTheme();

  // User menu anchor element — controls the MUI Menu open/close state
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);

  /*
   * --- Project Management State ---
   * `currentUser` is a local mirror of AuthContext's `user`, reshaped for child components.
   * `selectedProject` is the currently active project object (null = no project selected).
   * `users` is the full user list from backend, used for project member assignment UI.
   *
   * WARNING: `currentUser` is derived from `user` via useEffect. There is a brief
   * render cycle where `currentUser` is null even though `user` exists. Child components
   * must handle null currentUser gracefully.
   */
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [users, setUsers] = useState([]);
  const [selectedSoftware, setSelectedSoftware] = useState(null);
  const [activeDrawingId, setActiveDrawingId] = useState(null);

  // Initialize PostHog analytics on mount
  useEffect(() => {
    initPostHog();
  }, []);

  // Tour navigation hook — registration happens after handleSectionChange is defined (below)
  const { setNavigateToSection } = useTour();

  /*
   * Sync currentUser with AuthContext's user object.
   * This reshapes the auth user into the {id, name, role, email} format
   * expected by ProjectDashboard, ProjectView, and other child components.
   * Runs whenever the auth user changes (login, token refresh, etc.).
   */
  useEffect(() => {
    if (user) {
      setCurrentUser({
        id: user.user_id,
        name: user.username,
        role: user.role,
        email: user.email,
      });
      identifyUser(user);
    }
  }, [user]);

  // Track newly created drawings so the chat context includes the drawing_id
  // even before the CAD tab mounts or when the user is on a different tab.
  useEffect(() => {
    const handler = (e) => setActiveDrawingId(e.detail?.drawingId || null);
    window.addEventListener('cadDrawingCreated', handler);
    return () => window.removeEventListener('cadDrawingCreated', handler);
  }, []);

  // e-pilot navigation: when a tool creates/queries something, switch to the right section + tab
  useEffect(() => {
    const handler = (e) => {
      const { section, tab, entityId, entityType } = e.detail || {};
      if (section) {
        setActiveSection(section);
        localStorage.setItem('eis_activeSection', section);
        // Dispatch tab change for the target section to pick up
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('epilotTabChange', { detail: { tab, entityId, entityType } }));
        }, 300); // Wait for section to mount
      }
    };
    window.addEventListener('epilotNavigate', handler);
    return () => window.removeEventListener('epilotNavigate', handler);
  }, []);

  /*
   * Fetch all users from the backend for project member assignment dropdowns.
   * Array.isArray guard: the API may return non-array data on error or unexpected
   * response shape; defaulting to [] prevents downstream .map() crashes.
   * Fallback: if the API call fails entirely, include at least the current user
   * so the UI isn't completely empty.
   */
  useEffect(() => {
    // No team concept — only include the current user for project owner dropdown.
    // ACLManager fetches its own user list independently when needed.
    if (user) {
      setUsers([{
        id: user.user_id,
        name: user.username,
        role: user.role,
        email: user.email,
      }]);
    }
  }, [user]);

  /*
   * Fetch user's selected software preferences for the nav sidebar.
   * Synced with the SoftwareSelectionModal in OrchestratorView.
   */
  useEffect(() => {
    const fetchSoftwarePrefs = async () => {
      try {
        const response = await api.getUserProfile();
        setSelectedSoftware(response.data?.selected_software ?? null);
      } catch (err) {
        console.error('Failed to fetch software preferences:', err);
      }
    };
    if (user) {
      fetchSoftwarePrefs();
    }
  }, [user]);

  /*
   * Listen for software preference updates from OrchestratorView's modal.
   * When the user saves a new selection, we update the nav sidebar in sync.
   */
  useEffect(() => {
    const handlePrefsUpdated = (e) => {
      setSelectedSoftware(e.detail);
    };
    window.addEventListener('softwarePreferencesUpdated', handlePrefsUpdated);
    return () => window.removeEventListener('softwarePreferencesUpdated', handlePrefsUpdated);
  }, []);

  /*
   * --- Navigation State ---
   * `activeSection` determines which main content view is rendered.
   * Initialized from localStorage so the user returns to their last section on reload.
   * Valid values: 'projects', 'cad', 'simulations', 'documents', 'parts',
   *               'requirements', 'orchestrator', 'cadviewer'
   *
   * WARNING: Changing the key 'eis_activeSection' requires updating:
   *   - ErrorBoundary.js (resets it to 'projects' on crash)
   *   - Any other code referencing this key
   */
  const [activeSection, setActiveSection] = useState(() => {
    const saved = localStorage.getItem('eis_activeSection');
    // Fallback removed sections to 'projects'
    const removedSections = ['parts', 'simulations'];
    if (saved && removedSections.includes(saved)) return 'projects';
    return saved || 'projects';
  });
  const [currentBranch, setCurrentBranch] = useState(null);  // Branch ID (not name); null until API loads
  const [branches, setBranches] = useState([]);                // Full branch objects from api.getBranches()

  // Header Switcher State — allProjects feeds the HeaderProjectSwitcher dropdown
  const [allProjects, setAllProjects] = useState([]);
  const [recentProjectIds, setRecentProjectIds] = useState([]); // Up to 5 most recently visited project IDs

  // Auto-open the most recently created project when the tutorial starts
  // (must be after allProjects declaration to avoid temporal dead zone)
  useEffect(() => {
    const handler = () => {
      if (allProjects.length > 0) {
        const sorted = [...allProjects].sort((a, b) =>
          new Date(b.created_at || 0) - new Date(a.created_at || 0)
        );
        setSelectedProject(sorted[0]);
      }
    };
    window.addEventListener('tutorial:auto-open-project', handler);
    return () => window.removeEventListener('tutorial:auto-open-project', handler);
  }, [allProjects]);

  /**
   * Refresh the allProjects list from the API.
   * Called directly by ProjectDashboard after create/delete, and also
   * by the useEffect on refreshTrigger as a fallback.
   */
  const refreshAllProjects = async () => {
    try {
      const response = await api.getProjects();
      const projects = Array.isArray(response.data) ? response.data : [];
      setAllProjects(projects);

      // Clean up recentProjectIds — remove any IDs for deleted projects
      const projectIdSet = new Set(projects.map(p => p.project_id));
      setRecentProjectIds(prev => {
        const cleaned = prev.filter(id => projectIdSet.has(id));
        if (cleaned.length !== prev.length) {
          localStorage.setItem('recentProjects', JSON.stringify(cleaned));
        }
        return cleaned;
      });
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };


  /*
   * Session ID persistence — generates and stores a unique session identifier.
   *
   * Security behavior: if a different user logs in on the same browser, the old
   * session is discarded and a new one is generated. This prevents session leakage
   * between users sharing a machine.
   *
   * localStorage keys used:
   *   'eis_session_id'   — the session ID string
   *   'eis_session_user' — the user_id that owns this session
   *
   * WARNING: sessionId is passed to ChatContainer and CADViewerView. Changing
   * the generation format may affect backend session lookup/matching.
   */
  useEffect(() => {
    const STORAGE_KEY = 'eis_session_id';
    const USER_KEY = 'eis_session_user';

    const currentUserId = user?.user_id || null;
    const storedUserId = localStorage.getItem(USER_KEY);
    let storedSessionId = localStorage.getItem(STORAGE_KEY);

    // Generate new session if: no session exists, or different user logged in
    if (!storedSessionId || (currentUserId && storedUserId !== currentUserId)) {
      storedSessionId = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem(STORAGE_KEY, storedSessionId);
      if (currentUserId) {
        localStorage.setItem(USER_KEY, currentUserId);
      }
    }

    setSessionId(storedSessionId);
  }, [user?.user_id]);

  // Listen for chat session changes (e.g. "New Chat" button in ChatContainer)
  useEffect(() => {
    const handler = (e) => {
      const newId = e.detail?.sessionId;
      if (newId) setSessionId(newId);
    };
    window.addEventListener('chatSessionChanged', handler);
    return () => window.removeEventListener('chatSessionChanged', handler);
  }, []);

  /*
   * Fetch all projects on mount for the header project switcher.
   * Also restores the last selected project from localStorage so the user
   * returns to their previous project context on page reload.
   *
   * Array.isArray guard: protects against unexpected API response shapes.
   *
   * Also loads the recent projects list from localStorage for the quick-switch menu.
   *
   * Re-fetches whenever refreshTrigger changes (e.g. after project create/delete
   * in ProjectDashboard calls triggerRefresh()). Also loads recent project IDs
   * from localStorage on initial mount.
   */
  useEffect(() => {
    // Fetch projects and restore saved state on mount
    const init = async () => {
      await refreshAllProjects();

      // Restore last selected project from localStorage (initial mount only)
      const savedProjectId = localStorage.getItem('eis_selectedProjectId');
      if (savedProjectId) {
        const response = await api.getProjects();
        const projects = Array.isArray(response.data) ? response.data : [];
        const saved = projects.find(p => p.project_id === savedProjectId);
        if (saved) {
          setSelectedProject(saved);
        }
      }
    };
    init();

    // Load recent project IDs from localStorage
    const saved = localStorage.getItem('recentProjects');
    if (saved) {
      try {
        setRecentProjectIds(JSON.parse(saved));
      } catch (e) {
        console.error('Error parsing recent projects:', e);
      }
    }
  }, []);

  /*
   * Update the recent projects list (most recently used first, max 5).
   * Persists to localStorage key 'recentProjects' as a JSON array of project IDs.
   * Consumed by HeaderProjectSwitcher to show a "Recent" section.
   */
  const updateRecentProjects = (projectId) => {
    setRecentProjectIds(prev => {
      const updated = [projectId, ...prev.filter(id => id !== projectId)].slice(0, 5);
      localStorage.setItem('recentProjects', JSON.stringify(updated));
      return updated;
    });
  };

  /*
   * Handle project selection changes from the header switcher or project dashboard.
   * Updates selectedProject state AND persists the choice to localStorage.
   *
   * Side-effects:
   *   - Setting a project triggers the refreshBranches effect (via selectedProject dep).
   *   - All engineering views will re-render with the new projectId.
   *   - ChatContainer context object updates with the new project_id.
   *   - Clearing the project (null) removes the localStorage entry.
   */
  const handleProjectChange = (project) => {
    setSelectedProject(project);
    if (project) {
      updateRecentProjects(project.project_id);
      localStorage.setItem('eis_selectedProjectId', project.project_id);
      trackEvent('project_selected', { project_id: project.project_id, project_name: project.name });
    } else {
      localStorage.removeItem('eis_selectedProjectId');
    }
  };

  /*
   * Fetch branches for the currently selected project.
   * Called whenever selectedProject changes (via useEffect below).
   *
   * Branch selection priority:
   *   1. Saved branch from localStorage ('branch_<projectId>') if it still exists
   *   2. Current branch if it still exists in the new branch list
   *   3. The 'main' branch (by name)
   *   4. First branch in the list
   *
   * Array.isArray guard: protects against unexpected API response shapes.
   *
   * WARNING: This function reads `currentBranch` from closure at call time.
   * If called multiple times rapidly, stale closure values could cause incorrect fallback.
   */
  const refreshBranches = async () => {
    if (selectedProject) {
      try {
        const response = await api.getBranches(selectedProject.project_id);
        // Guard: ensure branchData is always an array
        const branchData = Array.isArray(response.data) ? response.data : [];
        setBranches(branchData);

        // Check for saved branch preference in localStorage (per-project key)
        const savedBranch = localStorage.getItem(`branch_${selectedProject.project_id}`);

        // Only set default if not already set or invalid
        // If we have a saved branch and it exists in the response, use it.
        if (savedBranch && branchData.find(b => b.branch_id === savedBranch)) {
            setCurrentBranch(savedBranch);
        } else if (!currentBranch || !branchData.find(b => b.branch_id === currentBranch)) {
             // Fallback: prefer 'dev' branch (writable), then first non-main, then main
             const devBranch = branchData.find(b => b.name === 'dev');
             const nonMainBranch = branchData.find(b => b.name !== 'main');
             if (devBranch) {
               setCurrentBranch(devBranch.branch_id);
             } else if (nonMainBranch) {
               setCurrentBranch(nonMainBranch.branch_id);
             } else if (branchData.length > 0) {
               setCurrentBranch(branchData[0].branch_id);
             }
        }
      } catch (error) {
        console.error('Error fetching branches:', error);
      }
    } else {
      // No project selected — reset branch state to defaults
      setBranches([]);
      setCurrentBranch(null);
    }
  };

  /*
   * Persist the current branch selection to localStorage whenever it changes.
   * Uses a dynamic key 'branch_<projectId>' so each project remembers its own branch.
   * WARNING: Changing this key format orphans previously saved preferences.
   */
  useEffect(() => {
      if (selectedProject && currentBranch) {
          localStorage.setItem(`branch_${selectedProject.project_id}`, currentBranch);
      }
  }, [currentBranch, selectedProject]);

  /*
   * Build breadcrumb trail for the current navigation state.
   * The first crumb is always "Evolunic" (resets to project list on click).
   * For the 'projects' section, it shows: Evolunic > Projects > [Project Name].
   * For other sections, it shows: Evolunic > [Section Label].
   */
  const getBreadcrumbs = () => {
    const crumbs = [
      { label: 'EIS', onClick: () => { setActiveSection('projects'); setSelectedProject(null); } }
    ];

    const sectionLabels = {
      projects: 'Projects',
      cad: 'CAD',
      documents: 'Documents',
      requirements: 'Requirements',
      compliance: 'Compliance',
      analytics: 'Analytics',
      'knowledge-base': 'Knowledge Base',
      orchestrator: 'Orchestrator',
      cadviewer: 'CAD Viewer',
    };

    if (activeSection === 'projects') {
      crumbs.push({
        label: 'Projects',
        onClick: () => { setSelectedProject(null); }
      });

      if (selectedProject) {
        crumbs.push({ label: selectedProject.name, onClick: null });
      }
    } else {
      crumbs.push({ label: sectionLabels[activeSection] || activeSection, onClick: null });
    }

    return crumbs;
  };

  /* Trigger branch fetch whenever the selected project changes */
  useEffect(() => {
    refreshBranches();
  }, [selectedProject]);

  /*
   * Handle file upload — appends to the uploadedFiles array.
   * These files are passed to ChatContainer so the AI chat has context about uploads.
   * NOTE: This only accumulates; there is no mechanism to remove files from the list.
   */
  const handleFileUploaded = (file) => {
    setUploadedFiles(prev => [...prev, file]);
    // Could trigger a refresh of search results or other actions
  };

  /* Legacy tab change handler — kept for TabPanel compatibility */
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  /*
   * Handle sidebar section navigation.
   * Persists the active section to localStorage for restore on reload.
   *
   * Side-effect: Navigating to 'projects' clears the selected project.
   * This means switching to another section and back to 'projects' will
   * deselect the current project. This is intentional — the projects section
   * shows the dashboard list view when no project is selected.
   */
  const handleSectionChange = (section) => {
    setActiveSection(section);
    localStorage.setItem('eis_activeSection', section);
    trackEvent('section_viewed', { section });
  };

  // Register section navigation callback so guided tours can switch views
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setNavigateToSection(handleSectionChange); }, []);

  /* ========================= RENDER ========================= */
  return (
    <>
      {/* Outer flex container: sidebar (GlobalNavigation) + main content area */}
      <Box sx={{ display: 'flex' }}>

        {/* --- Fixed App Bar (Header) --- */}
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            // z-index must be above the drawer so the header overlaps the sidebar
            zIndex: (t) => t.zIndex.drawer + 1,
            background: theme.palette.mode === 'dark'
              ? 'linear-gradient(90deg, #1e1e1e 0%, #121212 100%)'
              : 'linear-gradient(90deg, #ffffff 0%, #f8fafc 100%)',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Toolbar>
            {/* --- Logo and brand name --- */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 8px rgba(102, 126, 234, 0.35)',
                }}
              >
                <EvolunicLogo size={22} color="white" />
              </Box>
              <Typography
                variant="h6"
                noWrap
                component="div"
                sx={{
                  fontWeight: 700,
                  letterSpacing: '-0.5px',
                  background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                EIS
              </Typography>
            </Box>

            {/*
             * Project/Branch Quick Switcher in the header.
             * Receives allProjects, selectedProject, branches, and currentBranch
             * so the user can switch project/branch from anywhere without
             * navigating to the projects section.
             *
             * Data flow: user selection -> handleProjectChange/setCurrentBranch
             * -> re-renders all section views with new project/branch context.
             */}
            <Box data-tour="project-switcher">
            <HeaderProjectSwitcher
              projects={allProjects}
              selectedProject={selectedProject}
              onProjectChange={handleProjectChange}
              branches={branches}
              currentBranch={currentBranch}
              onBranchChange={setCurrentBranch}
              recentProjectIds={recentProjectIds}
            />
            </Box>

            {/* Spacer — pushes the right-side controls to the far right */}
            <Box sx={{ flexGrow: 1 }} />

            {/* --- Right side: dev mode badge, session ID, user menu --- */}
            <Box display="flex" alignItems="center" gap={2}>
              {/* Dev mode indicator — only visible when isDevMode is true from AuthContext */}
              {isDevMode && (
                <Box
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 2,
                    bgcolor: 'rgba(255, 152, 0, 0.1)',
                    border: '1px solid rgba(255, 152, 0, 0.3)',
                  }}
                >
                  <Typography variant="caption" sx={{ color: '#f57c00', fontWeight: 600 }}>
                    DEV MODE
                  </Typography>
                </Box>
              )}

              {/* EIS Credits balance — hidden when billing is disabled or account is exempt */}
              <CreditBalanceBadge />

              {/* Help Center button */}
              <HelpButton />

              {/*
               * User avatar + name — clickable to open the user dropdown menu.
               * Shows avatar image if user.avatar_url exists, otherwise shows
               * a gradient circle with the user's first initial.
               *
               * WARNING: Accesses currentUser.name directly — will throw if
               * currentUser is null and user.avatar_url is truthy. In practice
               * this is unlikely because avatar_url comes from the same user object.
               */}
              <Box
                data-tour="user-menu"
                onClick={(e) => setUserMenuAnchor(e.currentTarget)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 2,
                  bgcolor: 'rgba(0, 0, 0, 0.04)',
                  border: '1px solid',
                  borderColor: 'divider',
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: 'rgba(0, 0, 0, 0.08)',
                  },
                }}
              >
                {user?.avatar_url ? (
                  <Box
                    component="img"
                    src={user.avatar_url}
                    alt={currentUser.name}
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      color: 'white',
                    }}
                  >
                    {currentUser?.name?.charAt(0)?.toUpperCase() || '?'}
                  </Box>
                )}
                <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
                  {currentUser?.name || user?.username || 'Loading...'}
                </Typography>
              </Box>

              {/* User dropdown menu — settings + sign out */}
              <Menu
                anchorEl={userMenuAnchor}
                open={Boolean(userMenuAnchor)}
                onClose={() => setUserMenuAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                {/* User info header (non-interactive) */}
                <Box sx={{ px: 2, py: 1.5 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {user?.username || currentUser?.name || 'User'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {user?.email || currentUser?.email}
                  </Typography>
                </Box>
                <Divider />
                <MenuItem onClick={() => { setUserMenuAnchor(null); navigate('/settings'); }}>
                  <ListItemIcon>
                    <Settings fontSize="small" />
                  </ListItemIcon>
                  Settings
                </MenuItem>
                <MenuItem onClick={() => { setUserMenuAnchor(null); resetUser(); logout(); }}>
                  <ListItemIcon>
                    <Logout fontSize="small" />
                  </ListItemIcon>
                  Sign out
                </MenuItem>
              </Menu>
            </Box>
          </Toolbar>
        </AppBar>

        {/*
         * Left sidebar navigation.
         * Drives activeSection state which determines which main content view renders.
         * WARNING: Adding a new section here requires also adding a corresponding
         * conditional render block in the main content area below and a label
         * in getBreadcrumbs().sectionLabels.
         */}
        <GlobalNavigation
          activeSection={activeSection}
          onSectionChange={handleSectionChange}
        />

        {/* --- Main Content Area --- */}
        {/* mt: 8 offsets below the fixed AppBar; flexGrow: 1 fills remaining width */}
        <JobPollerProvider projectId={selectedProject?.project_id}>
        <Box component="main" sx={{ flexGrow: 1, p: 3, mt: 8 }}>
          <Container maxWidth={false}>

            {/* Breadcrumbs — only shown on the projects list view (not inside a project) */}
            {activeSection === 'projects' && !selectedProject && (
                <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
                {getBreadcrumbs().map((crumb, index) => {
                    const isLast = index === getBreadcrumbs().length - 1;
                    return isLast ? (
                        <Typography key={index} color="text.primary">
                            {crumb.label}
                        </Typography>
                    ) : (
                        <Link
                        key={index}
                        underline="hover"
                        color="inherit"
                        onClick={(e) => {
                            e.preventDefault();
                            if (crumb.onClick) crumb.onClick();
                        }}
                        href="#"
                        >
                        {crumb.label}
                        </Link>
                    );
                })}
                </Breadcrumbs>
            )}

            {/*
             * Two-column grid layout:
             *   Left (1fr)   — Main section content (projects, CAD, simulations, etc.)
             *   Right (400px) — Chat sidebar (always visible in authenticated view)
             *
             * On smaller screens (below md breakpoint), this collapses to a single column.
             */}
            <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { md: activeSection === 'orchestrator' ? '1fr' : '1fr 480px' } }}>
              {/* ===== Left Column: Section Content ===== */}
              <Suspense fallback={
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
                  <CircularProgress sx={{ color: '#667eea' }} />
                </Box>
              }>
              <Box>

                {/*
                 * Projects List — shown when activeSection is 'projects' and no project is selected.
                 * ProjectDashboard handles its own data fetching; receives currentUser and users
                 * for member assignment. onProjectSelect triggers handleProjectChange which
                 * sets selectedProject, persists to localStorage, and updates recent list.
                 */}
                {activeSection === 'projects' && !selectedProject && (
                    <Paper elevation={2} sx={{ mb: 3 }}>
                        <ProjectDashboard
                            onProjectSelect={handleProjectChange}
                            currentUser={currentUser}
                            onProjectsChanged={refreshAllProjects}
                        />
                    </Paper>
                )}

                {/*
                 * Project Detail View — shown when a specific project is selected.
                 * Receives branches and branch management callbacks so the user can
                 * create/switch/delete branches within this view.
                 *
                 * Data flow:
                 *   selectedProject -> ProjectView (displays project details)
                 *   onProjectUpdate -> updates selectedProject (e.g. after editing project name)
                 *   onBranchChange  -> setCurrentBranch (propagates to all engineering views)
                 *   onBranchesRefresh -> refreshBranches (re-fetches branch list from API)
                 */}
                {activeSection === 'projects' && selectedProject && (
                    <>
                    <Button
                      startIcon={<ArrowBack />}
                      onClick={() => setSelectedProject(null)}
                      sx={{
                        mb: 2,
                        color: '#667eea',
                        textTransform: 'none',
                        fontWeight: 500,
                        '&:hover': {
                          bgcolor: 'rgba(102, 126, 234, 0.08)',
                        },
                      }}
                    >
                      Back to Projects
                    </Button>
                    <Paper elevation={2} sx={{ mb: 3 }}>
                        <ProjectView
                            project={selectedProject}
                            currentUser={currentUser}
                            users={users}
                            onProjectUpdate={(updatedProject) => setSelectedProject(updatedProject)}
                            currentBranchId={currentBranch}
                            onBranchChange={setCurrentBranch}
                            branches={branches}
                            onBranchesRefresh={refreshBranches}
                        />
                    </Paper>
                    </>
                )}

                {/*
                 * Engineering Views — each renders when its activeSection matches.
                 * All receive projectId and branchId so they can scope their data.
                 *
                 * NOTE: projectId is optional (uses ?. operator). These views work
                 * without a selected project, showing either global data or an empty state.
                 *
                 * WARNING: If you add a new section view, also add its key to:
                 *   1. GlobalNavigation's section list
                 *   2. getBreadcrumbs().sectionLabels
                 *   3. The conditional render block here
                 */}
                {activeSection === 'cad' && (
                    <UnifiedCADView
                        projectId={selectedProject?.project_id}
                        branchId={currentBranch}
                        onActiveDrawingChange={setActiveDrawingId}
                    />
                )}
                {activeSection === 'documents' && (
                    <Paper elevation={2} sx={{ mb: 3 }}>
                        <DocumentsView projectId={selectedProject?.project_id} branchId={currentBranch} />
                    </Paper>
                )}
                {activeSection === 'requirements' && (
                    <Paper elevation={2} sx={{ mb: 3 }}>
                        <RequirementsView projectId={selectedProject?.project_id} branchId={currentBranch} />
                    </Paper>
                )}
                {activeSection === 'compliance' && (
                    <Paper elevation={2} sx={{ mb: 3 }}>
                        <ComplianceView projectId={selectedProject?.project_id} branchId={currentBranch} />
                    </Paper>
                )}
                {activeSection === 'verification' && (
                    <Paper elevation={2} sx={{ mb: 3 }}>
                        <Suspense fallback={<Box p={4} display="flex" justifyContent="center"><CircularProgress /></Box>}>
                            <VerificationView projectId={selectedProject?.project_id} branchId={currentBranch} />
                        </Suspense>
                    </Paper>
                )}
                {activeSection === 'knowledge-base' && (
                    <Paper elevation={2} sx={{ mb: 3 }}>
                        <KnowledgeBaseView projectId={selectedProject?.project_id} branchId={currentBranch} />
                    </Paper>
                )}
                {activeSection === 'analytics' && (
                    <Paper elevation={2} sx={{ mb: 3 }}>
                        <Suspense fallback={<Box p={4} display="flex" justifyContent="center"><CircularProgress /></Box>}>
                            <AnalyticsView projectId={selectedProject?.project_id} />
                        </Suspense>
                    </Paper>
                )}
                {activeSection === 'orchestrator' && (
                    <OrchestratorView projectId={selectedProject?.project_id} branchId={currentBranch} />
                )}

                {/*
                 * CAD Viewer — full-featured 3D viewer.
                 * Unlike other views, also receives sessionId and userId because
                 * it integrates with the chat/agent system for CAD operations.
                 */}
                {activeSection === 'cadviewer' && (
                    <CADViewerView
                        sessionId={sessionId}
                        projectId={selectedProject?.project_id}
                        branchId={currentBranch}
                        userId={currentUser?.id || user?.user_id}
                    />
                )}
              </Box>
              </Suspense>

              {/*
               * ===== Right Column: Chat Sidebar =====
               * Always rendered in the authenticated view regardless of active section.
               * Receives the current context (branch, project, user) so AI responses
               * are scoped to what the user is working on.
               *
               * Data flow:
               *   sessionId      -> ties messages to this browser session
               *   uploadedFiles  -> provides file context to the AI
               *   activeSection  -> lets the AI know what view the user is looking at
               *   context object -> branch_id, project_id, user_id for scoped queries
               */}
              <Paper data-tour="chat-container" elevation={2} sx={{ p: 0, height: 'calc(100vh - 88px)', display: activeSection === 'orchestrator' ? 'none' : 'flex', flexDirection: 'column', overflow: 'hidden', position: 'sticky', top: 80 }}>
                <ChatContainer
                    sessionId={sessionId}
                    uploadedFiles={uploadedFiles}
                    activeSection={activeSection}
                    context={{
                        branch_id: currentBranch,
                        project_id: selectedProject?.project_id,
                        user_id: currentUser?.id || user?.user_id,
                        drawing_id: activeDrawingId,
                        active_section: activeSection
                    }}
                />
              </Paper>
            </Box>
          </Container>
        </Box>

      <BottomActivityPanel projectId={selectedProject?.project_id} activeSection={activeSection} />
      </JobPollerProvider>
      </Box>
      <WelcomeDialog onCreateProject={() => {
        setActiveSection('projects');
        setSelectedProject(null);
      }} />
      <OnboardingChecklist onNavigate={(section) => {
        setActiveSection(section);
        if (section === 'projects') setSelectedProject(null);
      }} />
      <SpotlightGuide />
      <ProjectTutorial />
    </>
  );
}

export default App;
